<template>
  <div class="rte">
    <div v-if="editor" class="rte__toolbar">
      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
          title="Negrito"
        >B</button>
        <button
          type="button"
          class="rte__btn rte__btn--italic"
          :class="{ 'rte__btn--active': editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Itálico"
        >I</button>
        <button
          type="button"
          class="rte__btn rte__btn--underline"
          :class="{ 'rte__btn--active': editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
          title="Sublinhado"
        >U</button>
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive('heading', { level: 2 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          title="Título"
        >H2</button>
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          title="Subtítulo"
        >H3</button>
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Lista"
        >• Lista</button>
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Lista numerada"
        >1. Lista</button>
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive({ textAlign: 'left' }) }"
          @click="editor.chain().focus().setTextAlign('left').run()"
          title="Alinhar à esquerda"
        >&#8676;</button>
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().setTextAlign('center').run()"
          title="Centralizar"
        >&#8596;</button>
        <button
          type="button"
          class="rte__btn"
          :class="{ 'rte__btn--active': editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().setTextAlign('right').run()"
          title="Alinhar à direita"
        >&#8677;</button>
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          @click="setLink"
          :class="{ 'rte__btn--active': editor.isActive('link') }"
          title="Link"
        >🔗</button>
        <button
          type="button"
          class="rte__btn"
          v-if="editor.isActive('link')"
          @click="editor.chain().focus().unsetLink().run()"
          title="Remover link"
        >✕🔗</button>
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          @click="triggerImageUpload"
          title="Inserir imagem"
        >🖼</button>
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          style="display:none"
          @change="handleImageUpload"
        />
      </div>

      <div class="rte__toolbar-group">
        <button
          type="button"
          class="rte__btn"
          @click="editor.chain().focus().undo().run()"
          :disabled="!editor.can().undo()"
          title="Desfazer"
        >↩</button>
        <button
          type="button"
          class="rte__btn"
          @click="editor.chain().focus().redo().run()"
          :disabled="!editor.can().redo()"
          title="Refazer"
        >↪</button>
      </div>
    </div>

    <editor-content :editor="editor" class="rte__content" />
  </div>
</template>

<script>
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { ref, watch } from 'vue';
import api from '@/api';

export default {
  name: 'RichTextEditor',
  components: { EditorContent },
  props: {
    modelValue: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const imageInput = ref(null);

    const editor = useEditor({
      content: props.modelValue,
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Link.configure({ openOnClick: false }),
        Image.configure({ inline: false, allowBase64: false }),
      ],
      onUpdate({ editor }) {
        emit('update:modelValue', editor.getHTML());
      },
    });

    watch(() => props.modelValue, (val) => {
      if (editor.value && val !== editor.value.getHTML()) {
        editor.value.commands.setContent(val, false);
      }
    });

    const setLink = () => {
      const url = window.prompt('URL do link:');
      if (url) {
        editor.value.chain().focus().setLink({ href: url }).run();
      }
    };

    const triggerImageUpload = () => {
      imageInput.value?.click();
    };

    const handleImageUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const res = await api.uploadImagem(file);
        const url = res.data?.url;
        if (url && editor.value) {
          editor.value.chain().focus().setImage({ src: url }).run();
        }
      } catch (err) {
        alert('Erro ao enviar imagem: ' + (err.response?.data?.message || err.message));
      } finally {
        e.target.value = '';
      }
    };

    return { editor, setLink, imageInput, triggerImageUpload, handleImageUpload };
  },
};
</script>

<style scoped src="./style.css"></style>
